import { DASHBOARD_BOARD_NODE } from "./dashboard-board";
import { MONEY_ARTICLE_SERVICE_NODE } from "./money-article-service-contract";
import { FINANCIAL_PICTURE_NODE } from "./financial-picture-contract";
import { MANAGEMENT_REPORT_NODE } from "./management-report-contract";
import { EXECUTIVE_REPORT_NODE } from "./executive-report-contract";
import { EXPENSE_ANALYSIS_NODE } from "./expense-analysis-contract";
import { MONEY_ARTICLE_NODE } from "./money-article-contract";
import { FINANCIAL_STUDY_NODE } from "./financial-study-contract";
import { ANALYTICAL_REPORT_NODE } from "./analytical-report-contract";
import { FINANCE_REPORT_NODE } from "./finance-report-contract";

/**
 * Единый реестр блоков документа страницы.
 *
 * Зачем он есть. Всё, что редактор знает о блоке, раньше лежало отдельными
 * списками в разных местах: подпись для плашки перетаскивания, значок этой
 * плашки, «сжимать ли блок при перетаскивании», «можно ли свернуть»,
 * «можно ли поставить реакцию», «ставить курсор НА блок или в текст», «нужны
 * ли ему товары старого движка». Чтобы добавить один блок, надо было вписать
 * его в семь мест, и забытая строка давала тихий баг: блок без превью при
 * перетаскивании, курсор не туда, страница зря тянет каталог товаров.
 *
 * Теперь это одна строка таблицы на тип узла. Списки, которые нужны редактору
 * и серверу, выводятся из неё.
 *
 * Здесь ТОЛЬКО данные: ни React, ни узлов TipTap. Файл читают и клиент
 * (редактор), и сервер (план данных страницы), поэтому тянуть в него код
 * блоков нельзя.
 *
 * Что НЕ живёт здесь:
 *  - каталог меню вставки: там пункты, а не типы узлов (несколько пунктов
 *    создают один и тот же узел с разными настройками), и у него свои
 *    описания, значки и ключевые слова;
 *  - настройки блока по умолчанию (фабрика `createBlockNode` в редакторе);
 *  - оформление миниатюры при перетаскивании — CSS в `app/globals.css`.
 *
 * Добавляя блок: строка сюда + узел в `extensions` редактора + пункт меню.
 * Тест `tests/workspace-block-registry.test.ts` не даст забыть первое.
 */

/** Значок на плашке «поднятого блока» при перетаскивании. */
export type WorkspaceBlockDragIcon = "table" | "chart" | "data" | "plain";

/** Static silhouettes only; shared by templates and saved page previews.
 * A new block automatically gets a neutral panel until given its own model. */
export const WORKSPACE_BLOCK_PREVIEW_MODELS = [
  "heading", "text", "list", "checklist", "callout", "divider", "code", "toggle",
  "columns", "image", "file", "link", "tabs", "panel", "button", "table",
  "metrics", "kpi", "chart-line", "chart-bar", "chart-hbar", "funnel", "goal",
  "ranking", "calendar", "kanban", "map", "timeline", "feed", "product",
] as const;
export type WorkspaceBlockPreviewModel = typeof WORKSPACE_BLOCK_PREVIEW_MODELS[number];

export type WorkspaceBlockMeta = {
  /** Название для человека: плашка перетаскивания и меню действий блока. */
  label: string;
  /** Lightweight schematic appearance, without mounting the real block. */
  preview: WorkspaceBlockPreviewModel;
  /** Блок можно свернуть до начала (пункт «Свернуть» и кнопка «Раскрыть»). */
  collapsible: boolean;
  /** Блоку можно поставить эмодзи-реакцию. */
  reactions: boolean;
  /** Высокий блок: на время перетаскивания сжимается в миниатюру. */
  heavy: boolean;
  /** Курсор встаёт НА блок целиком, а не в текст внутри него. */
  focusAsNode: boolean;
  dragIcon: WorkspaceBlockDragIcon;
  /** Блоку нужны товары старого монолитного движка (`legacy-models`). */
  legacyModels: boolean;
  /** Старые формулы нужны, но вместо товаров достаточно компактных итогов. */
  legacySummaryOnly: boolean;
};

const DEFAULTS: Omit<WorkspaceBlockMeta, "label"> = {
  preview: "panel",
  collapsible: false,
  reactions: false,
  heavy: false,
  focusAsNode: false,
  dragIcon: "plain",
  legacyModels: false,
  legacySummaryOnly: false,
};

type Entry = Partial<WorkspaceBlockMeta> & { label: string };

/** Текстовый блок: сворачивается и принимает реакции. */
const TEXT = { collapsible: true, reactions: true } as const;
/** Тяжёлый блок с данными: сжимается при перетаскивании, курсор встаёт на него. */
const DATA_BLOCK = { collapsible: true, heavy: true, focusAsNode: true, dragIcon: "data" } as const;

const BLOCKS: Record<string, Entry> = {
  // ── Текст и разметка ──────────────────────────────────────────────────
  paragraph: { label: "Текст", ...TEXT, preview: "text" },
  heading: { label: "Заголовок", ...TEXT, preview: "heading" },
  blockquote: { label: "Цитата", ...TEXT, preview: "callout" },
  bulletList: { label: "Список", ...TEXT, preview: "list" },
  orderedList: { label: "Список", ...TEXT, preview: "list" },
  taskList: { label: "Чек-лист", ...TEXT, preview: "checklist" },
  callout: { label: "Выноска", ...TEXT, preview: "callout" },
  horizontalRule: { label: "Разделитель", collapsible: true, preview: "divider" },
  codeBlock: { label: "Код", collapsible: true, preview: "code" },
  toggle: { label: "Секция", collapsible: true, preview: "toggle" },
  columnList: { label: "Колонки", collapsible: true, preview: "columns" },
  tableOfContents: { label: "Оглавление", collapsible: true, preview: "list" },
  syncedBlock: { label: "Синхронизированный блок", collapsible: true },
  flowButton: { label: "Кнопка", collapsible: true, preview: "button" },

  // ── Файлы и ссылки ────────────────────────────────────────────────────
  image: { label: "Картинка", collapsible: true, heavy: true, focusAsNode: true, preview: "image" },
  attachment: { label: "Файл", collapsible: true, heavy: true, focusAsNode: true, dragIcon: "data", preview: "file" },
  pageLink: { label: "Ссылка на страницу", collapsible: true, heavy: true, focusAsNode: true, dragIcon: "data", preview: "link" },
  pageTabs: { label: "Вкладки", collapsible: true, focusAsNode: true, dragIcon: "data", preview: "tabs" },
  bookmarks: { label: "Закладки", collapsible: true, heavy: true, focusAsNode: true, dragIcon: "data", preview: "link" },
  embed: { label: "Встроенная страница", collapsible: true, heavy: true, focusAsNode: true },
  neuroPage: { label: "Нейространица", ...DATA_BLOCK },
  neuroPageBeta: { label: "Нейространица Beta", ...DATA_BLOCK },
  neuroPageStudioBeta: { label: "Нейространица Studio Beta", ...DATA_BLOCK },
  neuroBlocksBeta: { label: "Нейроблоки Beta", ...DATA_BLOCK },
  pageComposer: { label: "Своя страница", ...DATA_BLOCK },
  workbenchPage: { label: "Рабочая страница", ...DATA_BLOCK },

  // ── Таблицы ───────────────────────────────────────────────────────────
  table: { label: "Таблица", collapsible: true, dragIcon: "table", preview: "table" },
  table2: { label: "Таблица", ...DATA_BLOCK, dragIcon: "table", preview: "table" },
  flowTable: { label: "Таблица конструктор", collapsible: true, heavy: true, dragIcon: "table", preview: "table" },
  smartTable: { label: "Таблица", ...DATA_BLOCK, dragIcon: "table", preview: "table" },
  analyticsBlock: { label: "Аналитика", ...DATA_BLOCK, preview: "table" },
  fullView: { label: "Таблица-вид", collapsible: true, heavy: true, dragIcon: "table", preview: "table" },
  dataTable: { label: "Таблица данных", collapsible: true, preview: "table" },
  warehouseTable: { label: "Склады", collapsible: true, heavy: true, preview: "table" },

  // ── Аналитика магазина ────────────────────────────────────────────────
  chartBlock: { label: "Диаграмма", ...DATA_BLOCK, dragIcon: "chart", preview: "chart-bar" },
  // Ряды берёт своим лёгким маршрутом (/api/store-daily-series): каталог и
  // модели магазина ему не нужны, поэтому страница из-за него их не грузит.
  storeDayChart: { label: "Заказы по дням", collapsible: true, dragIcon: "chart", preview: "chart-line" },
  // Цифры считает тот же лёгкий маршрут, что и график; модели не нужны.
  [DASHBOARD_BOARD_NODE]: { label: "Доска", ...DATA_BLOCK, preview: "metrics" },
  storeMetrics: { label: "Показатели магазина", collapsible: true, dragIcon: "data", preview: "metrics" },
  // Очередь по сроку до нуля; данные — тем же запросом, что у таблицы.
  storeRunout: { label: "Что закончится первым", ...DATA_BLOCK, preview: "ranking" },
  kpi: { label: "Показатель", collapsible: true, dragIcon: "data", legacyModels: true, legacySummaryOnly: true, preview: "kpi" },
  metrics: { label: "Метрики", collapsible: true, dragIcon: "data", legacyModels: true, legacySummaryOnly: true, preview: "metrics" },
  goal: { label: "Цель", collapsible: true, dragIcon: "data", legacyModels: true, legacySummaryOnly: true, preview: "goal" },
  productRanking: { label: "Топ товаров", ...DATA_BLOCK, preview: "ranking" },
  funnelBlock: { label: "Воронка", ...DATA_BLOCK, preview: "funnel" },
  storeSalesHistory: { label: "История продаж", ...DATA_BLOCK, preview: "chart-bar" },
  pulseTable: { label: "Рука на пульсе", ...DATA_BLOCK, preview: "table" },
  productHistory: { label: "История товаров", ...DATA_BLOCK, preview: "chart-bar" },
  financeReconciliation: { label: "Сверка с отчётом Ozon", ...DATA_BLOCK, preview: "chart-bar" },
  storeMoney: { label: "Деньги магазина", ...DATA_BLOCK, preview: "chart-bar" },
  [FINANCIAL_PICTURE_NODE]: { label: "Финансовая картина", ...DATA_BLOCK, preview: "chart-bar" },
  [MANAGEMENT_REPORT_NODE]: { label: "Управленческий разбор", ...DATA_BLOCK, preview: "chart-bar" },
  [EXECUTIVE_REPORT_NODE]: { label: "Доклад руководителю", ...DATA_BLOCK, preview: "chart-bar" },
  [EXPENSE_ANALYSIS_NODE]: { label: "Расходы магазина", ...DATA_BLOCK, preview: "chart-bar" },
  [MONEY_ARTICLE_NODE]: { label: "Разбор денег магазина", ...DATA_BLOCK, preview: "chart-bar" },
  [MONEY_ARTICLE_SERVICE_NODE]: { label: "Разбор денег · FrogStat", ...DATA_BLOCK, preview: "chart-bar" },
  [FINANCIAL_STUDY_NODE]: { label: "Финансовый доклад", ...DATA_BLOCK, preview: "chart-bar" },
  [ANALYTICAL_REPORT_NODE]: { label: "Аналитический доклад", ...DATA_BLOCK, preview: "chart-bar" },
  [FINANCE_REPORT_NODE]: { label: "Финансовый отчёт", ...DATA_BLOCK, preview: "chart-bar" },
  stockBoard: { label: "Доска остатков", ...DATA_BLOCK, preview: "kanban" },
  orderModel: { label: "Заказы FBO / FBS", collapsible: true, focusAsNode: true, dragIcon: "data", preview: "chart-bar" },
  // heavy намеренно нет: «Разбор магазина» единственный из блоков этой группы
  // не сжимался при перетаскивании. Похоже на недосмотр в прежнем списке, но
  // правка-перенос не должна менять поведение — оставлено как было.
  storeStrategy: { label: "Разбор магазина", collapsible: true, focusAsNode: true, dragIcon: "data", legacyModels: true },
  geoMap: { label: "География", collapsible: true, heavy: true, dragIcon: "data", preview: "map" },
  flowFeed: { label: "Лента событий", collapsible: true, heavy: true, dragIcon: "data", legacyModels: true, preview: "feed" },
  tasksBlock: { label: "Задачи", collapsible: true, heavy: true, focusAsNode: true, dragIcon: "data", preview: "kanban" },
  storeCalendar: { label: "Календарь магазина", ...DATA_BLOCK, preview: "calendar" },
  productCard: { label: "Товар", collapsible: true, focusAsNode: true, preview: "product" },
  miniView: { label: "Мини-вид", collapsible: true, focusAsNode: true, preview: "table" },
  /** Легаси-тип старых сохранённых блоков: узла с таким именем уже нет. */
  product: { label: "Товар", preview: "product" },

  // ── Реклама ───────────────────────────────────────────────────────────
  flowAds: { label: "Реклама", collapsible: true, heavy: true, dragIcon: "table", legacyModels: true, preview: "table" },
  adAutopilot: { label: "Автопилот рекламы", ...DATA_BLOCK },
  adAutopilotInline: { label: "Автопилот рекламы · компактный", ...DATA_BLOCK },
  adAutopilotV2: { label: "Автопилот рекламы — версия 2", ...DATA_BLOCK },

  // ── Поставки ──────────────────────────────────────────────────────────
  supplyPlanning: { label: "Планирование поставки", ...DATA_BLOCK, preview: "table" },
  activeSupplyProducts: { label: "Активные поставки", ...DATA_BLOCK, preview: "table" },
  recentSupplies: { label: "Последние поставки", ...DATA_BLOCK, preview: "timeline" },
  supplyTimeline: { label: "Таймлайн поставок", ...DATA_BLOCK, preview: "timeline" },
};

/** Типы узлов, описанные в реестре. */
export const WORKSPACE_BLOCK_TYPES_IN_REGISTRY = Object.keys(BLOCKS);

const META = new Map<string, WorkspaceBlockMeta>(
  Object.entries(BLOCKS).map(([type, entry]) => [type, { ...DEFAULTS, ...entry }]),
);

const UNKNOWN: WorkspaceBlockMeta = { ...DEFAULTS, label: "Блок" };

/**
 * Описание блока. Незнакомый тип получает нейтральное описание, а не ошибку:
 * старый сохранённый документ не должен переставать открываться из-за блока,
 * который когда-то убрали.
 */
export function workspaceBlockMeta(type: string | null | undefined): WorkspaceBlockMeta {
  return (type && META.get(type)) || UNKNOWN;
}

function typesWhere(predicate: (meta: WorkspaceBlockMeta) => boolean): string[] {
  return [...META.entries()].filter(([, meta]) => predicate(meta)).map(([type]) => type);
}

/** Типы, у которых есть сворачивание (глобальный атрибут `blockCollapsed`). */
export const WORKSPACE_COLLAPSIBLE_TYPES = typesWhere(meta => meta.collapsible);
/** Типы, которым можно поставить реакцию (глобальный атрибут `reactions`). */
export const WORKSPACE_REACTION_TYPES = typesWhere(meta => meta.reactions);
/** Типы, которые сжимаются в миниатюру на время перетаскивания. */
export const WORKSPACE_HEAVY_TYPES = typesWhere(meta => meta.heavy);
/** Типы, которым нужны товары старого движка. */
export const WORKSPACE_LEGACY_MODEL_TYPES = typesWhere(meta => meta.legacyModels);

/**
 * Обычный текст: абзац, заголовок, список, цитата, код, выноска, разделитель.
 * Всё остальное — блок с данными или контейнер (таблица, график, колонки,
 * секция, картинка, вкладки…), который человек собирал руками.
 */
const PLAIN_TEXT_TYPES = new Set([
  "paragraph", "heading", "blockquote", "bulletList", "orderedList",
  "taskList", "codeBlock", "horizontalRule", "callout",
]);

/**
 * Блок, потерю которого нельзя допустить молча: его удаление подтверждается.
 *
 * Считается ТОЛЬКО то, что описано в этом реестре и не является обычным
 * текстом. Внутренности узлов сюда не попадают — ни `text`, ни `listItem`,
 * ни `tableRow`, ни `column`, ни упоминание в строке: их в реестре нет.
 * Это важно, иначе любое выделение текста выглядело бы как выделенный блок.
 *
 * Новый блок не останется без защиты: `tests/workspace-block-registry.test.ts`
 * не даёт добавить узел в редактор, не вписав его сюда.
 */
export function isWorkspaceDataBlockType(type: string | null | undefined): boolean {
  return !!type && META.has(type) && !PLAIN_TEXT_TYPES.has(type);
}
